import { derived, type Readable } from 'svelte/store';
import { app, type PermissionsType } from '../../../stores/app';

/**
 * Klucz i18n opisujący poziom uprawnień zalogowanego użytkownika (US-017).
 *
 * Słownictwo jest wspólne z kontrolką trójstanową panelu user_permissions (US-014): ten sam stan
 * uprawnień musi nazywać się w obu miejscach identycznie, więc korzystamy z tych samych kluczy
 * ('No access', 'Read only', 'Read and write'), a rola user_manager dokłada do nich zarządzanie
 * uprawnieniami.
 *
 * Klucze są wypisane jako pełne literały, a nie składane z fragmentów - dzięki temu da się je
 * znaleźć zwykłym grepem przy uzupełnianiu plików locale (US-019), a tłumacz widzi całą frazę
 * i może ją przestawić zgodnie z gramatyką swojego języka.
 *
 * Zwraca null, gdy uprawnienia nie zostały jeszcze wczytane - wiersz pokazuje wtedy 'No data'
 * zamiast zgadywać poziom dostępu.
 */
export function permissionSummaryKey(permissions: PermissionsType | null): string | null {
    if (permissions === null) {
        return null;
    }

    if (!permissions.canRead) {
        return 'No access';
    }

    if (permissions.isUserManager) {
        return permissions.canWrite
            ? 'Read and write, permission management'
            : 'Read only, permission management';
    }

    return permissions.canWrite ? 'Read and write' : 'Read only';
}

/** Klucz i18n poziomu uprawnień zalogowanego użytkownika, null dopóki nie są wczytane. */
export const permissionSummary: Readable<string | null> = derived(app, $app =>
    permissionSummaryKey($app.permissions)
);

/** Etykieta wiersza w ustawieniach konta; ten sam klucz co nazwa strony panelu (US-014). */
export const permissionSummaryLabel = 'User permissions';
